import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Flag, TrendUp, Warning, Trophy, CheckCircle } from "@phosphor-icons/react";

export default function SprintReport() {
  const { pid, sid } = useParams();
  const [report, setReport] = useState(null);
  const [burndown, setBurndown] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [r, b] = await Promise.all([
          api.get(`/sprints/${sid}/report`),
          api.get(`/sprints/${sid}/burndown`).catch(() => ({ data: null })),
        ]);
        setReport(r.data);
        setBurndown(b.data);
      } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, [sid]);

  if (error) return <div className="p-12 text-sm text-[#DC2626]">{error}</div>;
  if (!report) return <div className="p-12 text-sm text-muted-foreground">Loading…</div>;

  const creepClass = report.scope_creep_pct >= 30 ? "text-[#DC2626]" : report.scope_creep_pct >= 10 ? "text-[#D97706]" : "text-[#059669]";
  const compClass = report.completion_pct >= 80 ? "text-[#059669]" : report.completion_pct >= 50 ? "text-[#D97706]" : "text-[#DC2626]";

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-6">
        <Link to={`/projects/${pid}/sprints`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#002FA7]">
          <ArrowLeft size={14} weight="bold" /> Back to sprints
        </Link>
        <div className="mt-2 flex items-center gap-3">
          <Flag size={22} weight="bold" className="text-[#002FA7]" />
          <div>
            <h1 className="font-display text-3xl font-black">{report.sprint_name}</h1>
            <div className="text-xs uppercase font-mono-mini text-muted-foreground">
              Sprint report · status {report.status}
              {report.end_date && ` · ends ${report.end_date}`}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-12 space-y-8">
        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Completion" value={`${report.completion_pct}%`} sub={`${report.done_points} / ${report.total_points} pts`} className={compClass} icon={<CheckCircle size={16} weight="bold" />} />
          <Kpi label="Total tasks" value={report.total_tasks} sub={`${report.total_points} pts total`} />
          <Kpi label="Scope creep" value={`${report.scope_creep_pct}%`} sub={`+${report.added_after_start_count} tasks (${report.added_after_start_points} pts) added mid-sprint`} className={creepClass} icon={<Warning size={16} weight="bold" />} />
          <Kpi label="Velocity" value={report.velocity_points || report.done_points} sub="delivered points" icon={<TrendUp size={16} weight="bold" />} />
        </div>

        {/* Burndown table (best-effort — we only have ideal-line data) */}
        {burndown?.ideal_line && (
          <section>
            <h2 className="font-display text-lg font-bold mb-3">Burndown</h2>
            <div className="border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase font-mono-mini">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-right px-3 py-2">Ideal remaining (pts)</th>
                    <th className="text-right px-3 py-2">Actual remaining today</th>
                  </tr>
                </thead>
                <tbody>
                  {burndown.ideal_line.map((d, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-1.5 font-mono-mini text-xs">{d.date}</td>
                      <td className="px-3 py-1.5 text-right">{d.ideal_points}</td>
                      <td className="px-3 py-1.5 text-right text-muted-foreground">
                        {d.date === burndown.current?.date ? <b className="text-[#0A0A0A]">{burndown.current.remaining_points}</b> : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Top contributors */}
        <section>
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Trophy size={16} weight="bold" /> Top contributors</h2>
          {report.top_contributors.length === 0 ? (
            <div className="text-sm text-muted-foreground">No completed tasks yet.</div>
          ) : (
            <div className="border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase font-mono-mini">
                  <tr>
                    <th className="text-left px-3 py-2">Contributor</th>
                    <th className="text-right px-3 py-2">Points delivered</th>
                  </tr>
                </thead>
                <tbody>
                  {report.top_contributors.map((c, i) => (
                    <tr key={c.user_id} className="border-t border-border" data-testid={`contrib-${c.user_id}`}>
                      <td className="px-3 py-2 font-semibold">
                        <span className="inline-block w-5 text-muted-foreground font-mono-mini">#{i + 1}</span>
                        {c.name}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-mini">{c.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, className = "", icon }) {
  return (
    <div className="border border-border p-4 bg-white">
      <div className="eyebrow text-[10px] flex items-center gap-1">{icon}{label}</div>
      <div className={`font-display text-3xl font-black leading-none mt-1 ${className}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
